import type { Decimal } from '../math/num';
import { D, mul, pow, max } from '../math/num';
import type { RngState } from '../math/rng';
import { weightedIndex, chance, nextInt } from '../math/rng';
import {
  ENEMY_MODELS,
  PALETTES,
  LOADOUTS,
  MODIFIERS,
  composeName,
  type UnitVisual,
} from '../data/enemies';

/** A spawned enemy with resolved stats (Decimals live in state). */
export interface ActiveEnemy {
  id: string;
  visual: UnitVisual;
  name: string;
  hp: Decimal;
  maxHp: Decimal;
  dmgPerSec: Decimal;
  scrapReward: Decimal;
  componentReward: Decimal;
  isBoss: boolean;
  /** size factor for the renderer. */
  size: number;
  /** Damage-type multipliers (<1 resists, >1 weak). */
  resist: { kinetic: number; energy: number; corrosive: number };
}

export const BOSS_EVERY = 10;
export const MAX_ENEMIES = 30;

/** Wave scaling curves — the exponential walls that drive upgrading/ascending.
 *  Exported so overlord stats derive from the same curves.
 *  P37 balance: HP growth (1.13) sits BELOW Scrap growth (1.15) by design;
 *  with part gains now diminishing, sustainable progress comes from spending
 *  income, not from one runaway build. The walls are bosses and overlords. */
export function waveHp(wave: number): Decimal {
  return mul(D(12), pow(D(1.13), wave - 1));
}
export function waveDmg(wave: number): Decimal {
  // Per-enemy; a wave can field many, so this is gentler than the single-enemy era.
  return mul(D(1.6), pow(D(1.095), wave - 1));
}
export function waveScrap(wave: number): Decimal {
  return mul(D(5), pow(D(1.15), wave - 1));
}

/** How many enemies a wave fields (bosses come alone). Ramps toward MAX_ENEMIES. */
export function waveEnemyCount(wave: number): number {
  if (wave % BOSS_EVERY === 0) return 1;
  return Math.max(1, Math.min(MAX_ENEMIES, 1 + Math.floor(wave * 0.6)));
}

/** Palette pick weights shift toward stronger bands as the wave climbs. */
function palettePool(wave: number): { ids: string[]; weights: number[] } {
  const ids = ['pal.factory', 'pal.refurb', 'pal.overclocked', 'pal.milspec', 'pal.bricked'];
  const t = wave / 30; // ramps over the first ~30 waves
  const weights = [
    Math.max(0.1, 6 - t * 5), // factory fades
    Math.max(0.3, 4 - t * 2),
    1 + t * 2, // overclocked rises
    0.4 + t * 2,
    0.05 + t * 1.2, // bricked, rare then common
  ];
  return { ids, weights };
}

function modelPool(tier: number): string[] {
  return Object.values(ENEMY_MODELS)
    .filter((m) => m.tier <= tier && m.cls !== 'mech') // mechs are boss-only
    .map((m) => m.id);
}

/** Boss model for a tier (the tier guardian). */
function bossModel(tier: number): string {
  if (tier >= 3) return 'mdl.warden';
  if (tier >= 2) return 'mdl.dogbot';
  return 'mdl.fridge'; // T1 mini-boss: a very large refrigerator
}

const LOADOUT_IDS = Object.keys(LOADOUTS);

export function spawnEnemy(wave: number, tier: number, rng: RngState, id: string): ActiveEnemy {
  const isBoss = wave % BOSS_EVERY === 0;

  const modelId = isBoss ? bossModel(tier) : (modelPool(tier)[nextInt(rng, 0, modelPool(tier).length - 1)] ?? 'mdl.vacuum');
  const model = ENEMY_MODELS[modelId] ?? ENEMY_MODELS['mdl.vacuum']!;

  const { ids: palIds, weights } = palettePool(wave);
  const paletteId = isBoss ? 'pal.bricked' : (palIds[weightedIndex(rng, weights)] ?? 'pal.factory');
  const palette = PALETTES[paletteId] ?? PALETTES['pal.factory']!;

  const loadoutId = LOADOUT_IDS[nextInt(rng, 0, LOADOUT_IDS.length - 1)] ?? 'ld.bare';
  const loadout = LOADOUTS[loadoutId]!;

  const modifierIds: string[] = [];
  if (isBoss) modifierIds.push('mod.giant');
  else if (chance(rng, 0.12)) {
    const pool = ['mod.swarm', 'mod.glitched', 'mod.charged'];
    modifierIds.push(pool[nextInt(rng, 0, pool.length - 1)]!);
  }
  let hpMod = 1, dmgMod = 1, sizeMod = 1, scrapMod = 1;
  for (const id of modifierIds) {
    const m = MODIFIERS[id];
    if (!m) continue;
    hpMod *= m.hpMult; dmgMod *= m.dmgMult; sizeMod *= m.sizeMult; scrapMod *= m.scrapMult;
  }

  const hp = mul(mul(mul(waveHp(wave), model.hpMult * palette.band * loadout.hpMult * hpMod), 1), isBoss ? 8 : 1);
  const dmg = mul(waveDmg(wave), model.dmgMult * Math.sqrt(palette.band) * loadout.dmgMult * dmgMod);
  const scrap = mul(waveScrap(wave), model.scrapMult * palette.band * scrapMod * (isBoss ? 6 : 1));

  // Components: rarer loot that feeds the Mind. Bosses always drop; others ~14%.
  let components = D(0);
  if (isBoss) {
    components = max(D(1), mul(D(1), pow(D(1.08), wave - 1)));
  } else if (chance(rng, 0.14)) {
    components = max(D(1), mul(D(0.5), pow(D(1.06), wave - 1)));
  }

  // resistances: model base, with armored palettes hardening kinetic.
  const resist = { kinetic: 1, energy: 1, corrosive: 1 };
  if (model.resist) for (const k of ['kinetic', 'energy', 'corrosive'] as const) if (model.resist[k] !== undefined) resist[k] = model.resist[k]!;
  if (paletteId === 'pal.milspec') resist.kinetic *= 0.8;
  if (paletteId === 'pal.bricked') { resist.kinetic *= 0.85; resist.energy *= 0.85; resist.corrosive *= 0.85; }

  const visual: UnitVisual = { modelId, paletteId, loadoutId, modifierIds };
  return {
    id,
    visual,
    name: composeName(visual),
    hp,
    maxHp: hp,
    dmgPerSec: dmg,
    scrapReward: scrap,
    componentReward: components,
    isBoss,
    size: model.cls === 'drone' ? 0.8 * sizeMod : 1 * sizeMod,
    resist,
  };
}
